/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Where else this request sends the same value, and where it disagrees with itself** (`#944`,
 * slice `1e`).
 *
 * A realistic ORTB body threads one signal through `device`, `imp[].video` and `ext`, and a query
 * parameter can carry it again. Editing those one at a time is not only slow: a missed occurrence
 * leaves a request that disagrees with itself — `#927`'s defect produced by hand. This module is one
 * index over the **whole request** that answers three questions:
 *
 * 1. **How many places send this value?** — *in 3 places*.
 * 2. **Does this row disagree with another that should carry the same thing?** — *conflicts with …*.
 * 3. **Changing it, which other places change with it?** — the *Apply to all* gesture.
 *
 * ## One index, all three surfaces (Todd, 2026-09-18)
 *
 * The defect is a request disagreeing with itself, and a request is the parameters, the headers and
 * the body together — so a count taken per editor would call a `{ua}` in a parameter and again in the
 * body *used once* twice over. The page builds one index; each editor lays its own **live** rows over
 * it ({@link withSurface}), so a row the operator has just added is counted before it is saved.
 *
 * ## What counts as the same value
 *
 * Keyed by the value, never by its spelling — *one value, one wire representation, whichever way the
 * row names it*. `{ua}` and `session.user.userAgent` are one key, because a macro is sugar over the path
 * its concept declares (`X27`). A transform or an extraction makes a **different** value and so a
 * different key. A default is not part of the key: two rows reading the same path are the same
 * signal, and a disagreement between their defaults is precisely what the count helps find.
 *
 * Deliberately **not** counted: a suppressed row (it sends nothing), an empty one, and a shaped
 * expression — a conditional or a calculation is its author's own composition, and calling two of
 * them *the same value* is a claim this cannot check. A shared **reference** is counted by the entry
 * it addresses, which is the one case where *the same* is exact.
 *
 * ## What counts as *should be the same* — the declared concept (Todd, 2026-09-18)
 *
 * Two rows **conflict** when their destinations declare they carry the same session concept —
 * `DestinationField.value: { from: 'derived', source }` — and they send different values. Never
 * inferred from a name: a guessed link reports a conflict between correct rows, and *a false positive
 * is worse than an omission* (`#1002`). So a row counts only where its destination has declared the
 * link: every VAST parameter the catalog knows, the ORTB body fields `1e` declared (`4a`'s first step,
 * pulled forward for this), and an ORTB parameter through the body field it fills (`ORTB_PARAM_ROUTES`).
 * **A header joins through the driver's own declaration** (`DRIVER_HEADER_FIELDS`, `4b-ii`):
 * `User-Agent` carries `ua` and `X-Forwarded-For` carries `ip`, so an authored `X-Forwarded-For`
 * that disagrees with `device.ip` is `#1006`'s case, reported. Any other header declares nothing, so it
 * is counted and never in conflict.
 */

import { canonicalizeContextPath } from '@context-roots';
import { catalogEntryFor } from '@field-catalog';
import { getCatalogField, ortbParamRoute } from '@ortb-field-catalog';
import { MACRO_VOCABULARY } from '../inputs/data-model-schema';
import { macroTokenName } from './mapping-value-round-trip';
import { applyLeafToHeaderRow, headerRowToLeaf, type HeaderRow } from './headers-editor.adapters';
import { applyLeafToRow, rowToLeaf, type ParameterRow } from './parameter-editor.adapters';
import { paramCatalogEntry } from './param-field-catalog';
import {
  catalogSegments,
  leavesToTemplate,
  pathToLabel,
  templateToLeaves,
  type MappingLeaf,
} from './ortb-body-editor.adapters';
import { driverHeaderField, type DestinationField } from '@destination-field';
import type { OrtbBodyTemplate } from '../../../types/demand-source';
import type { DemandSourceMethod } from '../../../types';

export type RequestSurface = 'params' | 'headers' | 'ortbBody';

/** One place a value is sent, named the way the operator would find it. */
export interface OccurrencePlace {
  surface: RequestSurface;
  /** The row's own name on its surface — `pid`, `X-Forwarded-For`, `device.ua`. */
  name: string;
}

interface Entry extends OccurrencePlace {
  /** What it sends, or `null` where that is not one comparable value. */
  key: string | null;
  /** The session concept its destination declares it carries, or `null` where none is declared. */
  concept: string | null;
}

/** A change to one shared value, for {@link RequestOccurrences.applyElsewhere}. */
export interface ValueChange {
  /** The value every place being changed sends today. */
  fromKey: string;
  /** The row whose value they take — only its value is taken, never its name or its flags. */
  to: MappingLeaf;
  /** The surface the change was made on, which has applied it to its own rows already. */
  except: RequestSurface;
}

/**
 * The index, and the one way to change the places it names on other surfaces.
 *
 * Built by {@link buildRequestOccurrences}, read by {@link occurrenceMark} and {@link requestConflicts}.
 */
export interface RequestOccurrences {
  readonly entries: readonly Entry[];
  readonly method: DemandSourceMethod | undefined;
  /**
   * **Change the other surfaces' copies of a value** — required and nullable. `null` is a caller that
   * can reach no surface but the one being edited, so *Apply to all* changes that surface only and
   * says so.
   */
  readonly applyElsewhere: ElsewhereApplier | null;
}

/**
 * How a caller changes the copies on other surfaces, and **the sentence that says what that does** —
 * carried together so the question the operator answers is true of the caller they are in (`#1045`):
 * the detail page stages into sections, the wizard writes its one draft.
 */
export interface ElsewhereApplier {
  apply: (change: ValueChange) => void;
  /** Completes *Apply to all changes them too.* — e.g. *They open in their sections with the change staged…* */
  outcome: string;
}

/** The key for what a leaf sends, or `null` where it is not one comparable value. */
export function occurrenceKey(leaf: MappingLeaf): string | null {
  if (leaf.suppress) return null;
  if (leaf.kind === 'static') {
    const v = leaf.staticValue;
    if (v === undefined || v === null || v === '') return null;
    if (typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean') return null;
    return `literal:${String(v)}`;
  }
  if (leaf.expression?.type === 'macro') {
    const token = macroTokenName(`{${leaf.expression.name}}`);
    if (!token) return null;
    const path = MACRO_VOCABULARY.get(token)?.path;
    return path ? `path:${canonicalizeContextPath(path)}` : `macro:${token}`;
  }
  const expr = leaf.expression;
  if (!expr) return null;
  if (expr.type === 'reference') return expr.ref ? `ref:${expr.ref}` : null;
  if (expr.type === 'static') {
    const v = expr.value;
    return v === undefined || v === null || v === '' || typeof v === 'object' ? null : `literal:${String(v)}`;
  }
  if (expr.type === 'calculated') {
    if (!expr.path) return null;
    const parts = [`path:${canonicalizeContextPath(expr.path)}`];
    if (expr.transform) parts.push(`transform:${expr.transform}`);
    if (expr.regex) parts.push(`regex:${JSON.stringify(expr.regex)}`);
    return parts.join('|');
  }
  return null;
}

/** How a key reads in a sentence: `{ua}` is shown as the path it resolves to, a literal in quotes. */
function describeKey(key: string): string {
  const [head, ...mods] = key.split('|');
  const at = head.indexOf(':');
  const kind = head.slice(0, at);
  const rest = head.slice(at + 1);
  const base =
    kind === 'literal' ? `"${rest}"` : kind === 'macro' ? `{${rest}}` : kind === 'ref' ? 'a shared expression' : rest;
  const transform = mods.find((m) => m.startsWith('transform:'))?.slice('transform:'.length);
  const extracted = mods.some((m) => m.startsWith('regex:'));
  return [base, transform ? `(${transform})` : '', extracted ? '(extracted)' : ''].filter(Boolean).join(' ');
}

const derivedSource = (field: DestinationField | undefined): string | null =>
  field?.value?.from === 'derived' ? field.value.source : null;

function bodyConcept(leaf: MappingLeaf): string | null {
  const [section, ...rest] = catalogSegments(leaf.path);
  if (!section || rest.length === 0) return null;
  return derivedSource(getCatalogField(section, rest.join('.')));
}

/**
 * The concept a parameter row carries. On VAST it is the parameter catalog's declared link. On ORTB it
 * is the link of the **body field the parameter also fills** (`ORTB_PARAM_ROUTES`, D18) — so a `ua`
 * parameter and a `device.ua` leaf are one slot, as they are on the wire. The first target that
 * declares one answers: `w` fills the screen width, which declares none, and the player width, which
 * does.
 */
function paramConcept(method: DemandSourceMethod | undefined, name: string): string | null {
  if (method !== 'ORTB') return derivedSource(paramCatalogEntry(method, name));
  for (const target of ortbParamRoute(name)?.targets ?? []) {
    const concept = derivedSource(getCatalogField(target.section, target.name));
    if (concept) return concept;
  }
  return null;
}

const paramEntries = (method: DemandSourceMethod | undefined, rows: readonly ParameterRow[]): Entry[] =>
  rows
    .filter((row) => row.name.trim())
    .map((row) => ({
      surface: 'params',
      name: row.name.trim(),
      key: row.suppress ? null : occurrenceKey(rowToLeaf(row)),
      concept: paramConcept(method, row.name),
    }));
const headerEntries = (rows: readonly HeaderRow[]): Entry[] =>
  rows
    .filter((row) => row.key.trim())
    .map((row) => ({
      surface: 'headers',
      name: row.key.trim(),
      key: row.suppress ? null : occurrenceKey(headerRowToLeaf(row)),
      // The driver's own headers declare what they carry (`4b-ii`); any other header declares nothing.
      concept: derivedSource(driverHeaderField(row.key)),
    }));
const bodyEntries = (leaves: readonly MappingLeaf[]): Entry[] =>
  leaves.map((leaf) => ({
    surface: 'ortbBody',
    name: pathToLabel(leaf.path),
    key: occurrenceKey(leaf),
    concept: bodyConcept(leaf),
  }));

/**
 * Build the index for one request. Each surface is optional because a VAST source has no body.
 */
export function buildRequestOccurrences(
  request: {
    method: DemandSourceMethod | undefined;
    params?: readonly ParameterRow[];
    headers?: readonly HeaderRow[];
    ortbBody?: OrtbBodyTemplate | null;
  },
  applyElsewhere: ElsewhereApplier | null,
): RequestOccurrences {
  return {
    method: request.method,
    applyElsewhere,
    entries: [
      ...paramEntries(request.method, request.params ?? []),
      ...headerEntries(request.headers ?? []),
      ...bodyEntries(request.ortbBody ? templateToLeaves(request.ortbBody) : []),
    ],
  };
}

/**
 * **Lay an editor's live rows over the index** — its own surface replaced, the others kept.
 *
 * The page's index is built from what is on screen at the page's level, which for a surface being
 * edited lags the keystroke by a render. Without this, a row the operator just added reads *used
 * once* while its twin sits two rows above it.
 */
export function withSurface(
  index: RequestOccurrences,
  surface: RequestSurface,
  live: { params: readonly ParameterRow[] } | { headers: readonly HeaderRow[] } | { ortbBody: readonly MappingLeaf[] },
): RequestOccurrences {
  const own =
    'params' in live
      ? paramEntries(index.method, live.params)
      : 'headers' in live
        ? headerEntries(live.headers)
        : bodyEntries(live.ortbBody);
  return { ...index, entries: [...index.entries.filter((e) => e.surface !== surface), ...own] };
}

const SURFACE_WORD: Record<RequestSurface, string> = {
  params: 'query parameter',
  headers: 'header',
  ortbBody: 'body',
};

/** How a place is written in a sentence: `body device.ua`, `header X-Forwarded-For`. */
export function describePlace(place: OccurrencePlace): string {
  return `${SURFACE_WORD[place.surface]} ${place.name}`;
}

const samePlace = (a: OccurrencePlace, b: OccurrencePlace) => a.surface === b.surface && a.name === b.name;

/** Every other place sending the value this leaf sends. */
export function otherPlacesSending(
  index: RequestOccurrences | null,
  key: string,
  self: OccurrencePlace,
): OccurrencePlace[] {
  if (!index) return [];
  return index.entries.filter((e) => e.key === key && !samePlace(e, self));
}

/**
 * **The row's marks**: *in 3 places*, and *conflicts with …* where a row that should carry the same
 * concept sends something else. Either may be `null`.
 *
 * Words rather than a glyph (`×3`), because *a symbol is a name* and this row already renders `→` and
 * `⤵` with meanings of their own.
 */
export function occurrenceMark(
  index: RequestOccurrences | null,
  leaf: MappingLeaf,
  self: OccurrencePlace,
): {
  occurrences: { label: string; detail: string } | null;
  conflict: { label: string; detail: string } | null;
} {
  if (!index) return { occurrences: null, conflict: null };
  const key = leaf.suppress ? null : occurrenceKey(leaf);
  const same = key ? otherPlacesSending(index, key, self) : [];
  const occurrences =
    same.length > 0
      ? {
          label: `in ${same.length + 1} places`,
          detail: `The same value is also sent as: ${same.map(describePlace).join(', ')}.`,
        }
      : null;

  const me = index.entries.find((e) => samePlace(e, self));
  const concept = me?.concept ?? null;
  const disagree =
    key && concept
      ? index.entries.filter((e) => e.concept === concept && e.key && e.key !== key && !samePlace(e, self))
      : [];
  const conflict =
    disagree.length > 0
      ? {
          label:
            disagree.length === 1
              ? `conflicts with ${describePlace(disagree[0])}`
              : `conflicts with ${disagree.length} places`,
          detail: `${disagree.map((e) => `${describePlace(e)} sends ${describeKey(e.key!)}`).join('; ')}, for the same ${conceptWord(concept!)}.`,
        }
      : null;
  return { occurrences, conflict };
}

/** {@link occurrenceMark} in the shape `MappingRow`'s `marks` takes — absent where there is nothing to say. */
export function rowOccurrenceMarks(
  index: RequestOccurrences | null,
  leaf: MappingLeaf,
  self: OccurrencePlace,
): { occurrences?: { label: string; detail: string }; conflict?: { label: string; detail: string } } {
  const { occurrences, conflict } = occurrenceMark(index, leaf, self);
  return { ...(occurrences ? { occurrences } : {}), ...(conflict ? { conflict } : {}) };
}

/** The concept in words — the catalog's own description, lower-cased — or its name. */
function conceptWord(concept: string): string {
  const description = catalogEntryFor(concept)?.description;
  return description ? description.charAt(0).toLowerCase() + description.slice(1) : concept;
}

/** One disagreement: the places that should carry one concept and send different values. */
export interface RequestConflict {
  concept: string;
  /** In request order — parameters, headers, body — so the first is the one a Fix opens. */
  places: Array<OccurrencePlace & { sends: string }>;
  message: string;
}

/**
 * **Every place the request disagrees with itself**, one finding per concept (Todd, 2026-09-18).
 *
 * A warning, never an error: sending two values for one signal is legal and sometimes meant, and the
 * operator may know something the declaration does not. *Only this one* on the apply prompt is how an
 * operator makes one on purpose, and it deliberately does **not** clear the finding: the request still
 * disagrees with itself, and the finding is a fact about the request rather than about the gesture.
 */
export function requestConflicts(index: RequestOccurrences | null): RequestConflict[] {
  if (!index) return [];
  const byConcept = new Map<string, Entry[]>();
  for (const e of index.entries) {
    if (!e.concept || !e.key) continue;
    byConcept.set(e.concept, [...(byConcept.get(e.concept) ?? []), e]);
  }
  const conflicts: RequestConflict[] = [];
  for (const [concept, entries] of byConcept) {
    if (new Set(entries.map((e) => e.key)).size < 2) continue;
    const places = entries.map((e) => ({ surface: e.surface, name: e.name, sends: describeKey(e.key!) }));
    conflicts.push({
      concept,
      places,
      message:
        `These send different values for the ${conceptWord(concept)}: ` +
        `${places.map((p) => `${describePlace(p)} sends ${p.sends}`).join('; ')}. ` +
        'An exchange may read either, so the request disagrees with itself.',
    });
  }
  return conflicts;
}

/**
 * **The conflicts one surface takes part in**, as that surface's own findings — for an editor that
 * draws its own list (the wizard). Each points at **this surface's** first place, so its Fix opens a
 * row the editor holds; the detail page's list points at the first place in the whole request instead,
 * because it can open any section.
 */
export function surfaceConflictIssues(
  index: RequestOccurrences | null,
  surface: RequestSurface,
): Array<{ kind: 'conflict'; message: string; ref: string; fixable: true }> {
  return requestConflicts(index).flatMap((conflict) => {
    const mine = conflict.places.find((p) => p.surface === surface);
    return mine
      ? [{ kind: 'conflict' as const, message: conflict.message, ref: mine.name, fixable: true as const }]
      : [];
  });
}

/* ------------------------------------------------------------------ *
 * Apply to all — changing every copy of a value
 * ------------------------------------------------------------------ */

/**
 * A leaf carrying `to`'s **value** at `target`'s place: the value slots come across, and the place's
 * own path, importance and suppression stay. Provenance is dropped, because the operator authored this.
 */
export function withValueFrom(target: MappingLeaf, to: MappingLeaf): MappingLeaf {
  // Everything the place owns is kept by starting from it; only the value slots are replaced, and a
  // slot `to` leaves empty is emptied rather than inherited from what the place sent before.
  const next: MappingLeaf = {
    ...target,
    kind: to.kind,
    staticValue: to.staticValue,
    expression: to.expression,
    asType: to.asType,
    provenance: undefined,
  };
  for (const slot of ['staticValue', 'expression', 'asType', 'provenance'] as const) {
    if (next[slot] === undefined) delete next[slot];
  }
  return next;
}

/** Every parameter row sending `fromKey`, given `to`'s value. */
export function replaceValueInParams(
  rows: readonly ParameterRow[],
  change: Omit<ValueChange, 'except'>,
): ParameterRow[] {
  return rows.map((row) => {
    const leaf = rowToLeaf(row);
    return !row.suppress && occurrenceKey(leaf) === change.fromKey
      ? applyLeafToRow(row, withValueFrom(leaf, change.to))
      : row;
  });
}

/** Every header row sending `fromKey`, given `to`'s value. */
export function replaceValueInHeaders(rows: readonly HeaderRow[], change: Omit<ValueChange, 'except'>): HeaderRow[] {
  return rows.map((row) => {
    const leaf = headerRowToLeaf(row);
    return !row.suppress && occurrenceKey(leaf) === change.fromKey
      ? applyLeafToHeaderRow(row, withValueFrom(leaf, change.to))
      : row;
  });
}

/** Every body leaf sending `fromKey`, given `to`'s value. */
export function replaceValueInLeaves(
  leaves: readonly MappingLeaf[],
  change: Omit<ValueChange, 'except'>,
): MappingLeaf[] {
  return leaves.map((leaf) => (occurrenceKey(leaf) === change.fromKey ? withValueFrom(leaf, change.to) : leaf));
}

/** {@link replaceValueInLeaves} over a stored body template. */
export function replaceValueInBody<T extends OrtbBodyTemplate | undefined>(
  template: T,
  change: Omit<ValueChange, 'except'>,
): T {
  // An absent body stays absent, and a present one stays present — the return says which, so a
  // caller holding a body is not handed `undefined` to handle (the in-place section cannot, `4c`).
  if (!template) return template;
  return leavesToTemplate(replaceValueInLeaves(templateToLeaves(template), change), template.mode ?? 'merge') as T;
}
